import React from "react";
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// MarketVyne public legal/policy hub. Anchors used for deep-linking:
//   #tips, #refunds, #fees, #buyer-payments, #terms, #seller-earnings
// All copy is plain-language, no aspirational guarantees. Tip-fee text reads
// the live `platform_fee_percent` so admin changes propagate automatically.
export default function Policies() {
  const [feePct, setFeePct] = useState(17);
  useEffect(() => {
    axios.get(`${API}/settings/public`)
      .then(({ data }) => {
        const v = Number(data?.platform_fee_percent);
        if (Number.isFinite(v) && v > 0 && v < 100) setFeePct(v);
      })
      .catch(() => {});
  }, []);
  const keepPct = 100 - feePct;

  return (
    <div className="bg-white">
      <section className="bg-mv-secondary text-white py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-mv-accent mb-3">
            Legal & policies
          </div>
          <h1 className="font-display font-black text-4xl lg:text-5xl">
            MarketVyne policies
          </h1>
          <p className="text-white/70 mt-3 max-w-2xl">
            Plain-language rules for buyers, sellers, payments, tips, refunds,
            and platform fees. Sections jump-link from the buyer and seller flows.
          </p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 py-12 space-y-12">

        <section id="fees" data-testid="policy-fees">
          <h2 className="font-display font-bold text-2xl text-mv-secondary">Platform fees</h2>
          <p className="text-gray-700 mt-3 leading-relaxed">
            MarketVyne charges a flat <strong>{feePct}% platform fee</strong> on
            seller-side revenue. This applies to:
          </p>
          <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1">
            <li>The gross sale price of every order.</li>
            <li>The gross amount of every optional buyer tip.</li>
          </ul>
          <p className="text-gray-700 mt-3 leading-relaxed">
            Sellers therefore receive <strong>{keepPct}%</strong> of every
            order and <strong>{keepPct}%</strong> of every tip, before any
            applicable taxes, currency conversion, or Stripe Express payout fees
            that apply at the seller&rsquo;s connected account.
          </p>
        </section>

        <section id="buyer-payments" data-testid="policy-buyer-payments">
          <h2 className="font-display font-bold text-2xl text-mv-secondary">Buyer payments</h2>
          <p className="text-gray-700 mt-3 leading-relaxed">
            Buyer payments are processed through Stripe Checkout and are held by
            the platform until the order is approved or auto-released. The buyer
            sees the full breakdown of order price, any buyer service fee, and
            the total they will be charged before completing checkout.
          </p>
        </section>

        <section id="tips" data-testid="policy-tips">
          <h2 className="font-display font-bold text-2xl text-mv-secondary">Tipping policy</h2>
          <div className="space-y-3 mt-3 text-gray-700 leading-relaxed">
            <p><strong>1. Tips are optional.</strong> Buyers may, but are not
              required to, add a tip after the seller submits delivery.
              Sellers cannot pressure, demand, or require tips, and may not
              condition delivery, scope, or revisions on receiving one.</p>
            <p><strong>2. Tips are processed through MarketVyne.</strong> All tips
              are charged via Stripe Checkout exactly like ordinary order payments
              and are held to the same fraud, dispute, and abuse controls. Sellers
              may not solicit tips through PayPal, Venmo, cryptocurrency, wire
              transfers, or any other external payment method.</p>
            <p><strong>3. MarketVyne deducts a {feePct}% platform fee from
              tips.</strong> Sellers receive <strong>{keepPct}%</strong> of the
              gross tip. Payment-processing costs charged by Stripe may vary by
              payment method, country, account type, and pricing plan, and are
              recorded separately as a platform expense when available.</p>
            <p><strong>4. Tips do not purchase additional work.</strong> A tip
              does not entitle the buyer to extra revisions, priority, faster
              delivery, additional deliverables, expanded scope, or any
              modification to the original order.</p>
            <p><strong>5. Tip amounts.</strong> The minimum tip is <strong>$5</strong>
              and the maximum is <strong>$500</strong> per order. Only one
              successful tip per order is supported.</p>
            <p><strong>6. Tips generally cannot be refunded.</strong> Tips are
              voluntary and final. Exceptions where MarketVyne may refund
              part or all of a tip:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Duplicate or accidental charges.</li>
              <li>Fraud or unauthorized payment.</li>
              <li>Technical billing errors on our end.</li>
              <li>Admin-approved exceptions on a case-by-case basis.</li>
              <li>Where required by applicable law.</li>
            </ul>
            <p><strong>7. Review and reversal.</strong> MarketVyne may review,
              suspend, reverse, or refund any tip that appears to be the
              result of fraud, coercion, account compromise, or attempts to
              circumvent platform fees.</p>
            <p><strong>8. Tax reporting.</strong> Tips paid through MarketVyne
              count toward the seller&rsquo;s earnings on the platform and may be
              included in transaction history and tax reporting where required.</p>
          </div>
        </section>

        <section id="refunds" data-testid="policy-refunds">
          <h2 className="font-display font-bold text-2xl text-mv-secondary">Refunds</h2>
          <p className="text-gray-700 mt-3 leading-relaxed">
            Order refunds are handled through MarketVyne&rsquo;s dispute system or by
            admin review. Full or partial refunds reverse the seller transfer,
            the platform fee, and the buyer-service fee proportionally.
          </p>
          <p className="text-gray-700 mt-2 leading-relaxed">
            Tip refunds follow the limited exceptions described in
            <a href="#tips" className="text-mv-primary font-bold"> §Tipping policy</a>.
            When a tip is refunded after the seller has been paid out, MarketVyne
            may create a negative balance adjustment on the seller&rsquo;s account
            and contact the seller for resolution.
          </p>
        </section>

        <section id="seller-earnings" data-testid="policy-seller-earnings">
          <h2 className="font-display font-bold text-2xl text-mv-secondary">Seller earnings</h2>
          <p className="text-gray-700 mt-3 leading-relaxed">
            Order revenue and tip revenue are tracked separately in your seller
            dashboard. Both contribute to your <em>earnings balance</em> and
            <em> lifetime earnings</em>, but tip earnings are clearly labeled
            <strong> Tip from buyer</strong> in your transaction history. Payouts
            move funds from your earnings balance to your connected Stripe Express
            account using the platform&rsquo;s standard payout schedule.
          </p>
        </section>

        <section id="terms" data-testid="policy-terms">
          <h2 className="font-display font-bold text-2xl text-mv-secondary">Terms of service (summary)</h2>
          <p className="text-gray-700 mt-3 leading-relaxed">
            MarketVyne is a marketplace platform connecting buyers with
            independent sellers of digital services. By using MarketVyne you
            agree to use the platform in good faith, to keep payments and
            communications inside MarketVyne, to provide accurate listing and
            order information, and to comply with applicable laws and the
            tipping, refund, and fee policies above.
          </p>
          <p className="text-xs text-gray-400 mt-4">
            This is a plain-language summary. The full Terms of Service apply
            in case of conflict.
          </p>
        </section>

      </div>
    </div>
  );
}
